import React, { useContext, useEffect, useState } from "react";
import Button from "common-ui-components/components/button/Button";
import styles from "./Trailended.module.css";
import capterra from "./capterra.svg";
import g2 from "./g2-crowd-vector-logo-2022.svg";
import star from "./star.svg";
import Modal from "common-ui-components/components/modal/Modal";
import Subscription from "../Subscription";
import info from "common-ui-components/info-icon/info.svg";
import EM_API from "common-ui-components/components/utils/API";
import { notify } from "common-ui-components/components/notification/Notification";
import {
  Success,
  Failure,
} from "common-ui-components/components/notification/templates";
import TableManager from "common-ui-components/components/table-manager/TableManager";
import { getUserProducts } from "Main";
import ResponseLoader from "common-ui-components/components/ResponseLoader/ResponseLoader";
import { PRODUCT_CONTEXT } from "common-ui-components/components/utils/Config";
import { Products } from "ProductConfig";
import Offers from "../../offers/Offers";
import { isMspProduct, redirectToContactPage } from "utils/mspBasicUtils";
import { UserDetailsContext } from "App";
import Manage from "./Manage";

// Downgrade – UI UNCHANGED

export function Downgrade(plan = "Trial") {
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const columns = [
    { title: "Device Name", accessor: "deviceName" },
    { title: "Domain Name", accessor: "domainNetBiosName" },
    { title: "Logged On Users", accessor: "loggedOnUsers" },
    { title: "Agent Version", accessor: "agentVersion" },
    { title: "Last Contact Time", accessor: "lastContactedTime" },
  ];

  const handleDowngrade = async () => {
    const response = await EM_API.post("payment/downgrade/free", {
      deviceIds: [],
    });
    if (response.statusCode === 403) {
      setDeviceOpen(true);
      setShowTable(true);
    } else {
      window.location.reload();
    }
  };

  const handleSelectRecords = (selectedRecords) => {
    setSelectedDevices(selectedRecords.selected);
  };

  const handleDeviceID = async () => {
    const deviceIds = selectedDevices.map((device) => device.id);

    if (deviceIds.length === 0) {
      notify(<Failure>Please select at least one device.</Failure>);
      return;
    }

    try {
      const response = await EM_API.post("payment/downgrade/free", {
        deviceIds,
      });

      if (response.status.success) {
        notify(<Success>Devices successfully downgraded.</Success>);
        setDeviceOpen(false);
        setSelectedDevices([]);
        window.location.reload();
      } else {
        notify(
          <Failure>
            {response.status.message || "Failed to downgrade devices."}
          </Failure>,
        );
      }
    } catch (error) {
      console.error("Downgrade API call failed:", error);
      notify(<Failure>An error occurred while downgrading devices.</Failure>);
    }
  };

  return (
    <div className={styles.model}>
      <p className={styles.topic}>Continue with Downgrade ?</p>

      <div className={styles.modelHero}>
        <div className={styles.infoImg}>
          <img src={info} alt="info" />
        </div>
        <p>
          Free trial includes 25 devices and 1 technician. Please confirm to
          continue your with free trial.
        </p>
      </div>

      <section className={styles.buttonGroup}>
        <Button onClick={() => window.location.reload()}>Cancel</Button>
        <Button onClick={handleDowngrade} variant="primary">
          Continue
        </Button>
      </section>

      {showTable && (
        <Modal open={deviceOpen} setOpen={setDeviceOpen}>
          <div style={{ height: "90vh", width: "90vw", background: "#FFFFFF" }}>
            <div style={{ height: "95%", overflowY: "auto" }}>
              <TableManager
                columns={columns}
                url="devices/inventory-devices-v2?getAssociations=false&getDeviceInfo=true&getOsInfo=false&getNetworkInfo=true&getAgentInfo=false&id="
                headingText={`Total devices :`}
                rowContrast={false}
                selectable
                onSelectRecordCallback={handleSelectRecords}
              />
            </div>

            <div className={styles.buttonGrp}>
              <Button onClick={() => setDeviceOpen(false)}>Cancel</Button>
              <Button onClick={handleDeviceID} variant="primary">
                Proceed
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Trailended

const MAX_PRODUCTS_FOR_FREE_PLAN = 1;

const canSwitchToFree = (products) => {
  const ownedCount = Object.values(products).filter(Boolean).length;
  return ownedCount === MAX_PRODUCTS_FOR_FREE_PLAN;
};


const formatCount = (count) => {
  if (typeof count === "number" && count > 9) {
    return `${Math.floor(count / 10) * 10}+`;
  }
  return count;
};

const reviewPlatforms = [
  { name: "G2", logo: g2, width: 54, height: 54 },
  { name: "Capterra", logo: capterra, width: 100, height: 100 },
];

function Trailended() {
  const userDetails = useContext(UserDetailsContext);
  const [products, setProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);

  const productText = Products[PRODUCT_CONTEXT] || "Endpoint Manager";

  const [switchOpen, setSwitchOpen] = useState(false);
  const [data, setData] = useState({});
  const [openClaimOffer, setOpenClaimOffer] = useState(false);
  const plan = userDetails?.plan?.trim().toLowerCase();
  const showManageSubscription =
    Boolean(plan) && plan !== "free" && plan !== "trial";

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const result = await getUserProducts();
        setProducts(result);
      } catch (err) {
        console.error("Failed to fetch user products", err);
        setProductsError(true);
      } finally {
        setLoadingProducts(false);
      }
    };

    const fetchData = async () => {
      try {
        const response = await EM_API.get("/org/info");
        setData(response.body);
      } catch (error) {
        console.error("Failed to fetch organization info:", error);
      }
    };

    loadProducts();
    fetchData();
  }, []);

  if (loadingProducts) {
    return <ResponseLoader fullScreen />;
  }

  const handleUpgrade = () => {
    setShowSubscription(true);
  };

  const handleSwitchToFree = () => {
    setSwitchOpen(true);
  };

  const showSwitchToFree = !productsError && canSwitchToFree(products);

  let stats = [
    {
      count: formatCount(data.softwareCount || 0),
      detail: "Software Discovered",
      removeOnProducts: ["ra"],
    },
    {
      count: formatCount(data.hardwareCount || 0),
      detail: "Hardware Identified",
      removeOnProducts: ["ra"],
    },
    {
      count: formatCount(data.deviceCount || 0),
      detail: "Devices Enrolled",
    },
  ];
  stats = stats.filter((stat) =>
    stat.removeOnProducts
      ? !stat.removeOnProducts.includes(PRODUCT_CONTEXT)
      : true,
  );

  const handleZecuritPage = () => {
    if (isMspProduct()) {
      redirectToContactPage();
      return;
    }
    window.open("https://zecurit.com/pricing/", "_blank");
  };

  return (
    <div className={styles.clouser}>
      <div className={styles.container}>
        <div className={styles.heroSection}>
          <div className={styles.heroText}>
            <p className={styles.heading}>Your Plan Has Expired</p>
            <p className={styles.tagline}>
              Here's what you've accomplished with Zecurit {productText} so far
            </p>
          </div>

          <div className={styles.statsContainer}>
            {stats.map((item, index) => (
              <div
                key={item.detail}
                className={styles.statItem}
                style={
                  index !== stats.length - 1
                    ? { borderRight: "1px solid #EAECF0" }
                    : null
                }
              >
                <p className={styles.statCount}>{item.count}</p>
                <p className={styles.statDetail}>{item.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <section className={styles.actionSection}>
          <p className={styles.actionTitle}>
            Select how you’d like to continue using our platform
          </p>

          <p style={{ textAlign: "center" }}>
            <span className={styles.spanBold}>Upgrade to a paid Plan :</span>{" "}
            Visit our
            <span onClick={handleZecuritPage} className={styles.link}>
              {" "}
              pricing page{" "}
            </span>
            to compare plans and choose the plan that best suits your business
            needs.
          </p>
          {/* FIXME: @roy I have purposefully hidden the switch to free option for now in case u need it back just remove the comments from the components NEVER touch the logic again */}

          {/* {showSwitchToFree && (
            <p>
              <span className={styles.spanBold}>Switch to the free Plan:</span>{" "}
              You can continue using our product with limited features. Visit
              our{" "}
              <span onClick={handleZecuritPage} className={styles.link}>
                pricing page
              </span>{" "}
              to see what Free plan has to offer.
            </p>
          )} */}

          <div className={styles.btnGrp}>
            <Button onClick={handleUpgrade} variant="primary">
              {showManageSubscription ? "Manage" : "Buy Now"}
            </Button>
            {/* {showSwitchToFree && (
              <Button onClick={handleSwitchToFree}>Switch to free</Button>
            )} */}
          </div>

          <Modal open={switchOpen} setOpen={setSwitchOpen}>
            <div style={{ height: "100%", background: "inherit" }}>
              <Downgrade />
            </div>
          </Modal>
        </section>

        <section className={styles.reviewSection}>
          <p className={styles.daysWant}>Want 14 more days of your trial?</p>
          <p className={styles.reviewDes}>
            Leave us a quick review on Capterra or G2 and get 15 extra days to
            explore Zecurit — free of charge!
          </p>

          <div className={styles.reviewLogos}>
            {reviewPlatforms.map((platform) => (
              <div className={styles.reviewPlatform} key={platform.name}>
                <img
                  style={
                    platform.name === "Capterra" ? { marginTop: "-2rem" } : {}
                  }
                  src={platform.logo}
                  alt={`${platform.name} Logo`}
                  height={platform.height}
                  width={platform.width}
                />
                <div
                  style={
                    platform.name === "Capterra" ? { marginTop: "-0.8rem" } : {}
                  }
                  className={styles.stars}
                >
                  {Array.from({ length: 5 }).map((_, i) => (
                    <img key={i} src={star} alt="star" width={14} height={14} />
                  ))}
                </div>
                <p>Leave a Review</p>
              </div>
            ))}
          </div>

          {/*TODO:the claim offer page is not connected with the API*/}
          <div className={styles.btn}>
            <Button variant="primary" onClick={() => setOpenClaimOffer(true)}>
              Claim Offer
            </Button>
          </div>
        </section>
      </div>
      <Modal open={openClaimOffer} setOpen={setOpenClaimOffer}>
        <Offers />
      </Modal>
      <Modal
        open={showSubscription}
        setOpen={setShowSubscription}
        className={styles.subscriptionModal}
      >
        {showManageSubscription ? (
          <Manage modal setOpen={setShowSubscription} />
        ) : (
          <Subscription onBack={() => setShowSubscription(false)} popup />
        )}
      </Modal>
    </div>
  );
}

export function TechPlanEnded() {
  return (
    <div className={styles.techContainer}>
      <p className={styles.head}>Subscription Expired – Contact Super Admin</p>
      <p>
        Subscription renewal has failed and the grace period has ended. Please
        contact your super admin to renew or switch plans.
      </p>
    </div>
  );
}

export function BlockedMessagePage() {
  return (
    <div className={styles.techContainer}>
      <h1>Account Temporarily Suspended</h1>
      <p>
        Your account has been temporarily suspended while we verify a recent
        payment associated with your account.
        <br />
        Please check your email for details or contact support@zecurit.com to
        verify the payment and restore access.
      </p>
    </div>
  );
}

export default Trailended;